import React, { useState, memo } from 'react';
import { ShieldCheckIcon, CalendarCheckIcon, HomeIcon, BrainIcon, HeartHandshakeIcon, ArrowRightIcon, StarIcon, ClockIcon, UsersIcon, ChevronRightIcon } from 'lucide-react';
export function ServicesSection() {
  const [activeTab, setActiveTab] = useState<'hire' | 'care'>('hire');
  return <section className="w-full bg-white py-16 lg:py-24 border-b border-gray-100" aria-label="Services" data-id="element-380">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-381">
        {/* Section header */}
        <div className="text-center mb-10" data-id="element-382">
          <div className="inline-flex items-center gap-2 bg-soft-teal text-accent-teal-dark text-xs font-semibold px-3 py-1.5 rounded-full mb-4" data-id="element-383">
            <ShieldCheckIcon className="w-3.5 h-3.5" data-id="element-384" />
            Nurse-Led & Clinically Overseen
          </div>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-text-dark tracking-tight mb-3" data-id="element-385">
            Healthcare Staffing &{' '}
            <span className="text-medical-blue" data-id="element-386">Care Solutions</span>
          </h2>
          <p className="text-text-muted text-lg max-w-2xl mx-auto" data-id="element-387">
            Every professional on our platform is vetted and managed by a
            Registered Nurse and former PSW — ensuring clinical standards you
            can trust.
          </p>
        </div>

        {/* Tab switcher */}
        <div className="flex justify-center mb-10" data-id="element-388">
          <div className="inline-flex bg-gray-100 rounded-2xl p-1.5 gap-1" data-id="element-389">
            <button onClick={() => setActiveTab('hire')} className={`px-6 py-2.5 rounded-xl text-sm font-bold transition-all duration-200 ${activeTab === 'hire' ? 'bg-medical-blue text-white shadow-sm' : 'text-text-muted hover:text-text-dark'}`} aria-pressed={activeTab === 'hire'} data-id="element-390">
              Hire Now
            </button>
            <button onClick={() => setActiveTab('care')} className={`px-6 py-2.5 rounded-xl text-sm font-bold transition-all duration-200 ${activeTab === 'care' ? 'bg-medical-blue text-white shadow-sm' : 'text-text-muted hover:text-text-dark'}`} aria-pressed={activeTab === 'care'} data-id="element-391">
              Care Solutions
            </button>
          </div>
        </div>

        {/* Hire Now Grid */}
        {activeTab === 'hire' && <div data-id="element-392">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8" data-id="element-393">
              {/* RN Card */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-float overflow-hidden group" data-id="element-394">
                {/* Card top bar */}
                <div className="bg-medical-blue px-6 pt-6 pb-8 relative" data-id="element-395">
                  <div className="flex items-start justify-between mb-4" data-id="element-396">
                    <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center" data-id="element-397">
                      <span className="text-white font-extrabold text-xl" data-id="element-398">
                        RN
                      </span>
                    </div>
                    {/* Clinical Oversight Badge */}
                    <span className="flex items-center gap-1 bg-white/20 text-white text-[11px] font-bold px-2.5 py-1 rounded-full border border-white/30" data-id="element-399">
                      <ShieldCheckIcon className="w-3 h-3" data-id="element-400" />
                      Clinical Oversight
                    </span>
                  </div>
                  <h3 className="text-white font-extrabold text-xl leading-tight" data-id="element-401">
                    Registered Nurse
                  </h3>
                  <p className="text-white/70 text-sm mt-1" data-id="element-402">
                    Full scope of practice
                  </p>

                  {/* Decorative circle */}
                  <div className="absolute -bottom-6 -right-6 w-24 h-24 bg-white/10 rounded-full" data-id="element-403" />
                </div>

                {/* Card body */}
                <div className="px-6 py-5" data-id="element-404">
                  <div className="space-y-3 mb-5" data-id="element-405">
                    <div className="flex items-center gap-2 text-sm text-text-muted" data-id="element-406">
                      <StarIcon className="w-4 h-4 text-yellow-400 fill-yellow-400 flex-shrink-0" data-id="element-407" />
                      <span data-id="element-408">4.9★ avg. rating across 1,200+ shifts</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-text-muted" data-id="element-409">
                      <ClockIcon className="w-4 h-4 text-accent-teal flex-shrink-0" data-id="element-410" />
                      <span data-id="element-411">Day, evening & overnight shifts</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-text-muted" data-id="element-412">
                      <ShieldCheckIcon className="w-4 h-4 text-accent-teal flex-shrink-0" data-id="element-413" />
                      <span data-id="element-414">CNO registered & credential-verified</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-text-muted" data-id="element-415">
                      <UsersIcon className="w-4 h-4 text-accent-teal flex-shrink-0" data-id="element-416" />
                      <span data-id="element-417">Vetted by our RN clinical team</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between mb-4 py-3 border-t border-gray-100" data-id="element-418">
                    <div data-id="element-419">
                      <div className="text-xs text-text-muted font-medium" data-id="element-420">
                        Starting from
                      </div>
                      <div className="text-medical-blue font-extrabold text-xl" data-id="element-421">
                        $42
                        <span className="text-sm font-semibold text-text-muted" data-id="element-422">
                          /hr
                        </span>
                      </div>
                    </div>
                    <div className="text-right" data-id="element-423">
                      <div className="text-xs text-text-muted font-medium" data-id="element-424">
                        Available
                      </div>
                      <div className="flex items-center gap-1 text-accent-teal font-bold text-sm" data-id="element-425">
                        <span className="w-1.5 h-1.5 bg-accent-teal rounded-full live-dot" data-id="element-426" />
                        Now
                      </div>
                    </div>
                  </div>

                  <button className="w-full bg-medical-blue hover:bg-medical-blue-dark text-white font-bold py-3 rounded-xl transition-colors flex items-center justify-center gap-2 text-sm" data-id="element-427">
                    <CalendarCheckIcon className="w-4 h-4" data-id="element-428" />
                    View Availability
                  </button>
                </div>
              </div>

              {/* RPN Card */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-float overflow-hidden group" data-id="element-429">
                <div className="bg-[#6366f1] px-6 pt-6 pb-8 relative" data-id="element-430">
                  <div className="flex items-start justify-between mb-4" data-id="element-431">
                    <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center" data-id="element-432">
                      <span className="text-white font-extrabold text-xl" data-id="element-433">
                        RPN
                      </span>
                    </div>
                    <span className="flex items-center gap-1 bg-white/20 text-white text-[11px] font-bold px-2.5 py-1 rounded-full border border-white/30" data-id="element-434">
                      <ShieldCheckIcon className="w-3 h-3" data-id="element-435" />
                      Clinical Oversight
                    </span>
                  </div>
                  <h3 className="text-white font-extrabold text-xl leading-tight" data-id="element-436">
                    Registered Practical Nurse
                  </h3>
                  <p className="text-white/70 text-sm mt-1" data-id="element-437">
                    Skilled clinical support
                  </p>
                  <div className="absolute -bottom-6 -right-6 w-24 h-24 bg-white/10 rounded-full" data-id="element-438" />
                </div>

                <div className="px-6 py-5" data-id="element-439">
                  <div className="space-y-3 mb-5" data-id="element-440">
                    <div className="flex items-center gap-2 text-sm text-text-muted" data-id="element-441">
                      <StarIcon className="w-4 h-4 text-yellow-400 fill-yellow-400 flex-shrink-0" data-id="element-442" />
                      <span data-id="element-443">4.8★ avg. rating across 890+ shifts</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-text-muted" data-id="element-444">
                      <ClockIcon className="w-4 h-4 text-accent-teal flex-shrink-0" data-id="element-445" />
                      <span data-id="element-446">Flexible scheduling, all shifts</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-text-muted" data-id="element-447">
                      <ShieldCheckIcon className="w-4 h-4 text-accent-teal flex-shrink-0" data-id="element-448" />
                      <span data-id="element-449">CNO/CARNA registered & verified</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-text-muted" data-id="element-450">
                      <UsersIcon className="w-4 h-4 text-accent-teal flex-shrink-0" data-id="element-451" />
                      <span data-id="element-452">Vetted by our RN clinical team</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between mb-4 py-3 border-t border-gray-100" data-id="element-453">
                    <div data-id="element-454">
                      <div className="text-xs text-text-muted font-medium" data-id="element-455">
                        Starting from
                      </div>
                      <div className="text-[#6366f1] font-extrabold text-xl" data-id="element-456">
                        $36
                        <span className="text-sm font-semibold text-text-muted" data-id="element-457">
                          /hr
                        </span>
                      </div>
                    </div>
                    <div className="text-right" data-id="element-458">
                      <div className="text-xs text-text-muted font-medium" data-id="element-459">
                        Available
                      </div>
                      <div className="flex items-center gap-1 text-accent-teal font-bold text-sm" data-id="element-460">
                        <span className="w-1.5 h-1.5 bg-accent-teal rounded-full live-dot" data-id="element-461" />
                        Now
                      </div>
                    </div>
                  </div>

                  <button className="w-full bg-[#6366f1] hover:bg-[#4f46e5] text-white font-bold py-3 rounded-xl transition-colors flex items-center justify-center gap-2 text-sm" data-id="element-462">
                    <CalendarCheckIcon className="w-4 h-4" data-id="element-463" />
                    View Availability
                  </button>
                </div>
              </div>

              {/* PSW Card */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-float overflow-hidden group" data-id="element-464">
                <div className="bg-accent-teal px-6 pt-6 pb-8 relative" data-id="element-465">
                  <div className="flex items-start justify-between mb-4" data-id="element-466">
                    <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center" data-id="element-467">
                      <span className="text-white font-extrabold text-xl" data-id="element-468">
                        PSW
                      </span>
                    </div>
                    <span className="flex items-center gap-1 bg-white/20 text-white text-[11px] font-bold px-2.5 py-1 rounded-full border border-white/30" data-id="element-469">
                      <ShieldCheckIcon className="w-3 h-3" data-id="element-470" />
                      Clinical Oversight
                    </span>
                  </div>
                  <h3 className="text-white font-extrabold text-xl leading-tight" data-id="element-471">
                    Personal Support Worker
                  </h3>
                  <p className="text-white/70 text-sm mt-1" data-id="element-472">
                    Compassionate daily care
                  </p>
                  <div className="absolute -bottom-6 -right-6 w-24 h-24 bg-white/10 rounded-full" data-id="element-473" />
                </div>

                <div className="px-6 py-5" data-id="element-474">
                  <div className="space-y-3 mb-5" data-id="element-475">
                    <div className="flex items-center gap-2 text-sm text-text-muted" data-id="element-476">
                      <StarIcon className="w-4 h-4 text-yellow-400 fill-yellow-400 flex-shrink-0" data-id="element-477" />
                      <span data-id="element-478">4.7★ avg. rating across 2,100+ shifts</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-text-muted" data-id="element-479">
                      <ClockIcon className="w-4 h-4 text-accent-teal flex-shrink-0" data-id="element-480" />
                      <span data-id="element-481">24/7 availability, all regions</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-text-muted" data-id="element-482">
                      <ShieldCheckIcon className="w-4 h-4 text-accent-teal flex-shrink-0" data-id="element-483" />
                      <span data-id="element-484">Background-checked & insured</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-text-muted" data-id="element-485">
                      <UsersIcon className="w-4 h-4 text-accent-teal flex-shrink-0" data-id="element-486" />
                      <span data-id="element-487">Vetted by our RN & former PSW team</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between mb-4 py-3 border-t border-gray-100" data-id="element-488">
                    <div data-id="element-489">
                      <div className="text-xs text-text-muted font-medium" data-id="element-490">
                        Starting from
                      </div>
                      <div className="text-accent-teal font-extrabold text-xl" data-id="element-491">
                        $26
                        <span className="text-sm font-semibold text-text-muted" data-id="element-492">
                          /hr
                        </span>
                      </div>
                    </div>
                    <div className="text-right" data-id="element-493">
                      <div className="text-xs text-text-muted font-medium" data-id="element-494">
                        Available
                      </div>
                      <div className="flex items-center gap-1 text-accent-teal font-bold text-sm" data-id="element-495">
                        <span className="w-1.5 h-1.5 bg-accent-teal rounded-full live-dot" data-id="element-496" />
                        Now
                      </div>
                    </div>
                  </div>

                  <button className="w-full bg-accent-teal hover:bg-accent-teal-dark text-white font-bold py-3 rounded-xl transition-colors flex items-center justify-center gap-2 text-sm" data-id="element-497">
                    <CalendarCheckIcon className="w-4 h-4" data-id="element-498" />
                    View Availability
                  </button>
                </div>
              </div>
            </div>

            {/* Nurse-Led USP banner */}
            <div className="bg-soft-teal rounded-2xl px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-4" data-id="element-499">
              <div className="flex items-center gap-3" data-id="element-500">
                <div className="w-10 h-10 bg-accent-teal rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-501">
                  <ShieldCheckIcon className="w-5 h-5 text-white" data-id="element-502" />
                </div>
                <div data-id="element-503">
                  <div className="font-bold text-text-dark text-sm" data-id="element-504">
                    Clinical Oversight on Every Hire
                  </div>
                  <div className="text-text-muted text-sm" data-id="element-505">
                    All workers are screened and managed by our Registered Nurse
                    and former PSW leadership team — not HR generalists.
                  </div>
                </div>
              </div>
              <a href="#why-us" className="flex-shrink-0 flex items-center gap-1.5 text-medical-blue font-bold text-sm hover:underline" data-id="element-506">
                Learn more <ChevronRightIcon className="w-4 h-4" data-id="element-507" />
              </a>
            </div>
          </div>}

        {/* Care Solutions Menu */}
        {activeTab === 'care' && <div data-id="element-508">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8" data-id="element-509">
              {/* In-Home Nursing */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-float p-6 group cursor-pointer" data-id="element-510">
                <div className="w-14 h-14 bg-soft-teal rounded-2xl flex items-center justify-center mb-5" data-id="element-511">
                  <HomeIcon className="w-7 h-7 text-medical-blue" data-id="element-512" />
                </div>
                <div className="flex items-center gap-2 mb-2" data-id="element-513">
                  <h3 className="font-extrabold text-text-dark text-lg" data-id="element-514">
                    In-Home Nursing
                  </h3>
                  <span className="flex items-center gap-1 bg-soft-teal text-accent-teal-dark text-[10px] font-bold px-2 py-0.5 rounded-full" data-id="element-515">
                    <ShieldCheckIcon className="w-2.5 h-2.5" data-id="element-516" />
                    Clinical Oversight
                  </span>
                </div>
                <p className="text-text-muted text-sm leading-relaxed mb-5" data-id="element-517">
                  Professional nursing care delivered in the comfort of your
                  home. Wound care, medication management, post-surgical
                  support, and chronic disease monitoring.
                </p>
                <ul className="space-y-2 mb-6" data-id="element-518">
                  <li className="flex items-center gap-2 text-sm text-text-muted" data-id="element-519">
                    <span className="w-1.5 h-1.5 bg-medical-blue rounded-full flex-shrink-0" data-id="element-520" />
                    Post-surgical recovery care
                  </li>
                  <li className="flex items-center gap-2 text-sm text-text-muted" data-id="element-521">
                    <span className="w-1.5 h-1.5 bg-medical-blue rounded-full flex-shrink-0" data-id="element-522" />
                    Wound care & IV therapy
                  </li>
                  <li className="flex items-center gap-2 text-sm text-text-muted" data-id="element-523">
                    <span className="w-1.5 h-1.5 bg-medical-blue rounded-full flex-shrink-0" data-id="element-524" />
                    Medication administration
                  </li>
                  <li className="flex items-center gap-2 text-sm text-text-muted" data-id="element-525">
                    <span className="w-1.5 h-1.5 bg-medical-blue rounded-full flex-shrink-0" data-id="element-526" />
                    Chronic disease management
                  </li>
                </ul>
                <button className="w-full border-2 border-medical-blue text-medical-blue hover:bg-medical-blue hover:text-white font-bold py-3 rounded-xl transition-all flex items-center justify-center gap-2 text-sm" data-id="element-527">
                  Learn More
                  <ArrowRightIcon className="w-4 h-4" data-id="element-528" />
                </button>
              </div>

              {/* Dementia Care */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-float p-6 group cursor-pointer" data-id="element-529">
                <div className="w-14 h-14 bg-purple-50 rounded-2xl flex items-center justify-center mb-5" data-id="element-530">
                  <BrainIcon className="w-7 h-7 text-purple-600" data-id="element-531" />
                </div>
                <div className="flex items-center gap-2 mb-2" data-id="element-532">
                  <h3 className="font-extrabold text-text-dark text-lg" data-id="element-533">
                    Dementia Care
                  </h3>
                  <span className="flex items-center gap-1 bg-soft-teal text-accent-teal-dark text-[10px] font-bold px-2 py-0.5 rounded-full" data-id="element-534">
                    <ShieldCheckIcon className="w-2.5 h-2.5" data-id="element-535" />
                    Clinical Oversight
                  </span>
                </div>
                <p className="text-text-muted text-sm leading-relaxed mb-5" data-id="element-536">
                  Specialized memory care from workers trained in dementia and
                  Alzheimer's support. Person-centred approaches that preserve
                  dignity and quality of life.
                </p>
                <ul className="space-y-2 mb-6" data-id="element-537">
                  <li className="flex items-center gap-2 text-sm text-text-muted" data-id="element-538">
                    <span className="w-1.5 h-1.5 bg-purple-500 rounded-full flex-shrink-0" data-id="element-539" />
                    Alzheimer's & dementia support
                  </li>
                  <li className="flex items-center gap-2 text-sm text-text-muted" data-id="element-540">
                    <span className="w-1.5 h-1.5 bg-purple-500 rounded-full flex-shrink-0" data-id="element-541" />
                    Behavioural symptom management
                  </li>
                  <li className="flex items-center gap-2 text-sm text-text-muted" data-id="element-542">
                    <span className="w-1.5 h-1.5 bg-purple-500 rounded-full flex-shrink-0" data-id="element-543" />
                    Caregiver respite & relief
                  </li>
                  <li className="flex items-center gap-2 text-sm text-text-muted" data-id="element-544">
                    <span className="w-1.5 h-1.5 bg-purple-500 rounded-full flex-shrink-0" data-id="element-545" />
                    Safe wandering prevention
                  </li>
                </ul>
                <button className="w-full border-2 border-purple-600 text-purple-600 hover:bg-purple-600 hover:text-white font-bold py-3 rounded-xl transition-all flex items-center justify-center gap-2 text-sm" data-id="element-546">
                  Learn More
                  <ArrowRightIcon className="w-4 h-4" data-id="element-547" />
                </button>
              </div>

              {/* Personal Care Services */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-float p-6 group cursor-pointer" data-id="element-548">
                <div className="w-14 h-14 bg-rose-50 rounded-2xl flex items-center justify-center mb-5" data-id="element-549">
                  <HeartHandshakeIcon className="w-7 h-7 text-rose-500" data-id="element-550" />
                </div>
                <div className="flex items-center gap-2 mb-2" data-id="element-551">
                  <h3 className="font-extrabold text-text-dark text-lg" data-id="element-552">
                    Personal Care
                  </h3>
                  <span className="flex items-center gap-1 bg-soft-teal text-accent-teal-dark text-[10px] font-bold px-2 py-0.5 rounded-full" data-id="element-553">
                    <ShieldCheckIcon className="w-2.5 h-2.5" data-id="element-554" />
                    Clinical Oversight
                  </span>
                </div>
                <p className="text-text-muted text-sm leading-relaxed mb-5" data-id="element-555">
                  Compassionate daily living support from vetted PSWs. Bathing,
                  grooming, mobility assistance, meal preparation, and
                  companionship — all with clinical oversight.
                </p>
                <ul className="space-y-2 mb-6" data-id="element-556">
                  <li className="flex items-center gap-2 text-sm text-text-muted" data-id="element-557">
                    <span className="w-1.5 h-1.5 bg-rose-400 rounded-full flex-shrink-0" data-id="element-558" />
                    Bathing & personal hygiene
                  </li>
                  <li className="flex items-center gap-2 text-sm text-text-muted" data-id="element-559">
                    <span className="w-1.5 h-1.5 bg-rose-400 rounded-full flex-shrink-0" data-id="element-560" />
                    Mobility & transfer assistance
                  </li>
                  <li className="flex items-center gap-2 text-sm text-text-muted" data-id="element-561">
                    <span className="w-1.5 h-1.5 bg-rose-400 rounded-full flex-shrink-0" data-id="element-562" />
                    Meal prep & nutrition support
                  </li>
                  <li className="flex items-center gap-2 text-sm text-text-muted" data-id="element-563">
                    <span className="w-1.5 h-1.5 bg-rose-400 rounded-full flex-shrink-0" data-id="element-564" />
                    Companionship & social engagement
                  </li>
                </ul>
                <button className="w-full border-2 border-rose-500 text-rose-500 hover:bg-rose-500 hover:text-white font-bold py-3 rounded-xl transition-all flex items-center justify-center gap-2 text-sm" data-id="element-565">
                  Learn More
                  <ArrowRightIcon className="w-4 h-4" data-id="element-566" />
                </button>
              </div>
            </div>

            {/* Care Solutions USP banner */}
            <div className="bg-soft-teal rounded-2xl px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-4" data-id="element-567">
              <div className="flex items-center gap-3" data-id="element-568">
                <div className="w-10 h-10 bg-accent-teal rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-569">
                  <ShieldCheckIcon className="w-5 h-5 text-white" data-id="element-570" />
                </div>
                <div data-id="element-571">
                  <div className="font-bold text-text-dark text-sm" data-id="element-572">
                    Every Care Plan is Nurse-Supervised
                  </div>
                  <div className="text-text-muted text-sm" data-id="element-573">
                    Our founder — a Registered Nurse and former PSW — personally
                    oversees care quality standards across all service
                    categories.
                  </div>
                </div>
              </div>
              <a href="#why-us" className="flex-shrink-0 flex items-center gap-1.5 text-medical-blue font-bold text-sm hover:underline" data-id="element-574">
                Our approach <ChevronRightIcon className="w-4 h-4" data-id="element-575" />
              </a>
            </div>
          </div>}
      </div>
    </section>;
}